import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  Crown, CreditCard, Building, Landmark, ArrowDownToLine,
  ArrowUpFromLine, Plus, Trash2, Star, Shield, CheckCircle,
  Globe, Smartphone, Wallet, LogOut, User, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const PaymentsPage = () => {
  const { user, token, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fund');

  // Fund wallet form
  const [fundAmount, setFundAmount] = useState('');
  const [fundCurrency, setFundCurrency] = useState('USD');
  const [fundMethod, setFundMethod] = useState('stripe');
  const [fundWalletCurrency, setFundWalletCurrency] = useState('CBDC');
  const [fundProcessing, setFundProcessing] = useState(false);

  // Withdraw form
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawBankId, setWithdrawBankId] = useState('');
  const [withdrawWalletCurrency, setWithdrawWalletCurrency] = useState('CBDC');
  const [withdrawProcessing, setWithdrawProcessing] = useState(false);

  // Add bank form
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_name: '', bank_name: '', account_number: '',
    routing_number: '', swift_code: '', account_type: 'checking',
    currency: 'USD', country: 'US'
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    try {
      const [accountsRes, walletsRes, historyRes] = await Promise.all([
        axios.get(`${API}/bank-accounts`, { headers, withCredentials: true }),
        axios.get(`${API}/wallets`, { headers, withCredentials: true }),
        axios.get(`${API}/payments/history`, { headers, withCredentials: true })
      ]);
      setBankAccounts(accountsRes.data);
      setWallets(walletsRes.data);
      setPaymentHistory(historyRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check for Stripe return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const funded = searchParams.get('funded');
    if (sessionId && funded === 'true') {
      (async () => {
        try {
          const res = await axios.get(`${API}/payments/fund-status/${sessionId}`, { headers, withCredentials: true });
          if (res.data.status === 'completed') {
            toast.success(`Wallet funded successfully! $${res.data.amount} deposited.`);
            fetchData();
          }
        } catch { /* ignore */ }
      })();
    }

    // Check for PayPal return
    const paypalSuccess = searchParams.get('paypal_success');
    const paypalToken = searchParams.get('token'); // PayPal appends token=ORDER_ID
    const txId = searchParams.get('tx');
    if (paypalSuccess === 'true' && paypalToken) {
      (async () => {
        try {
          const res = await axios.post(`${API}/payments/paypal/capture/${paypalToken}`, {}, { headers, withCredentials: true });
          if (res.data.status === 'completed') {
            toast.success(`PayPal payment captured! $${res.data.amount} credited to wallet.`);
          } else if (res.data.status === 'already_completed') {
            toast.info('This payment was already processed.');
          }
          fetchData();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to capture PayPal payment');
        }
      })();
    }
    const paypalCancel = searchParams.get('paypal_cancel');
    if (paypalCancel === 'true') {
      toast.info('PayPal payment was cancelled.');
    }
  }, [searchParams]);

  const handleFundWallet = async () => {
    if (!fundAmount || parseFloat(fundAmount) <= 0) return toast.error('Enter a valid amount');
    setFundProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/fund-wallet`, {
        amount: parseFloat(fundAmount),
        currency: fundCurrency,
        payment_method: fundMethod,
        wallet_currency: fundWalletCurrency
      }, { headers, withCredentials: true });

      if (res.data.checkout_url) {
        // Redirect to Stripe or PayPal checkout
        window.location.href = res.data.checkout_url;
      } else if (res.data.status === 'completed') {
        toast.success(res.data.message || 'Payment processed successfully!');
        setFundAmount('');
        fetchData();
      } else {
        toast.error('No checkout URL returned. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setFundProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return toast.error('Enter a valid amount');
    if (!withdrawBankId) return toast.error('Select a bank account');
    setWithdrawProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/withdraw`, {
        amount: parseFloat(withdrawAmount),
        bank_account_id: withdrawBankId,
        wallet_currency: withdrawWalletCurrency
      }, { headers, withCredentials: true });
      toast.success(`${res.data.amount} ${res.data.currency} withdrawn to ${res.data.bank_account}`);
      setWithdrawAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawProcessing(false);
    }
  };

  const handleAddBank = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bank-accounts`, bankForm, { headers, withCredentials: true });
      toast.success('Bank account linked successfully!');
      setAddBankOpen(false);
      setBankForm({ account_name: '', bank_name: '', account_number: '', routing_number: '', swift_code: '', account_type: 'checking', currency: 'USD', country: 'US' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bank account');
    }
  };

  const handleDeleteBank = async (accountId) => {
    try {
      await axios.delete(`${API}/bank-accounts/${accountId}`, { headers, withCredentials: true });
      toast.success('Bank account removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove bank account');
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await axios.put(`${API}/bank-accounts/${accountId}/primary`, {}, { headers, withCredentials: true });
      toast.success('Primary bank account updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update primary account');
    }
  };

  const cbdcWallet = wallets.find(w => w.currency === 'CBDC');
  const kalWallet = wallets.find(w => w.currency === 'TNB');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="payments-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-black" />
              </div>
              <span className="font-serif text-lg font-bold hidden sm:block">Royal CBDC</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/citizen" className="text-sm font-bold text-foreground hover:text-gold">Dashboard</Link>
              <Link to="/tnb/market" className="text-sm font-bold text-foreground hover:text-gold">TANUB</Link>
              <Link to="/wallet" className="text-sm font-bold text-foreground hover:text-gold">Wallet</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-serif text-2xl md:text-3xl font-normal">Payments & Banking</h1>
          <p className="text-muted-foreground mt-1">Fund your wallet, manage bank accounts, and withdraw funds</p>
        </div>

        {/* Wallet Balances */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="bg-black text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-xs uppercase tracking-wider">CBDC Balance</p>
                  <p className="font-mono text-3xl font-bold mt-1" data-testid="cbdc-balance">
                    {cbdcWallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-gold" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-black text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-xs uppercase tracking-wider">TNB Balance</p>
                  <p className="font-mono text-3xl font-bold mt-1" data-testid="tnb-balance">
                    {kalWallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>
                <CreditCard className="h-8 w-8 text-gold" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Panel */}
          <div className="lg:col-span-2">
            <Card className="sovereign-card">
              <CardContent className="p-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3 mb-6">
                    <TabsTrigger value="fund" data-testid="tab-fund">
                      <ArrowDownToLine className="h-4 w-4 mr-2" /> Fund Wallet
                    </TabsTrigger>
                    <TabsTrigger value="withdraw" data-testid="tab-withdraw">
                      <ArrowUpFromLine className="h-4 w-4 mr-2" /> Withdraw
                    </TabsTrigger>
                    <TabsTrigger value="banks" data-testid="tab-banks">
                      <Building className="h-4 w-4 mr-2" /> Bank Accounts
                    </TabsTrigger>
                  </TabsList>

                  {/* Fund Wallet Tab */}
                  <TabsContent value="fund">
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number" step="0.01" placeholder="Enter amount"
                          value={fundAmount} onChange={e => setFundAmount(e.target.value)}
                          data-testid="fund-amount-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <Select value={fundCurrency} onValueChange={setFundCurrency}>
                            <SelectTrigger data-testid="fund-currency-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="USD">USD</SelectItem>
                              <SelectItem value="EUR">EUR</SelectItem>
                              <SelectItem value="GBP">GBP</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Credit To</Label>
                          <Select value={fundWalletCurrency} onValueChange={setFundWalletCurrency}>
                            <SelectTrigger data-testid="fund-wallet-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CBDC">CBDC Wallet</SelectItem>
                              <SelectItem value="TNB">TNB Wallet</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Payment Method</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => setFundMethod('stripe')}
                            className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                              fundMethod === 'stripe' ? 'border-gold bg-gold/5' : 'border-border hover:border-border/80'
                            }`}
                            data-testid="method-stripe"
                          >
                            <CreditCard className={`h-6 w-6 ${fundMethod === 'stripe' ? 'text-gold' : 'text-muted-foreground'}`} />
                            <div className="text-left">
                              <p className="font-semibold text-sm">Stripe</p>
                              <p className="text-xs text-muted-foreground">Card / Bank</p>
                            </div>
                            {fundMethod === 'stripe' && <CheckCircle className="h-4 w-4 text-gold ml-auto" />}
                          </button>
                          <button
                            onClick={() => setFundMethod('paypal')}
                            className={`p-4 rounded-lg border-2 transition-all flex items-center gap-3 ${
                              fundMethod === 'paypal' ? 'border-blue-500 bg-blue-500/5' : 'border-border hover:border-border/80'
                            }`}
                            data-testid="method-paypal"
                          >
                            <Globe className={`h-6 w-6 ${fundMethod === 'paypal' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                            <div className="text-left">
                              <p className="font-semibold text-sm">PayPal</p>
                              <p className="text-xs text-muted-foreground">PayPal Balance</p>
                            </div>
                            {fundMethod === 'paypal' && <CheckCircle className="h-4 w-4 text-blue-500 ml-auto" />}
                          </button>
                        </div>
                      </div>
                      {fundAmount && parseFloat(fundAmount) > 0 && (
                        <div className="p-4 rounded-lg bg-muted/50" data-testid="fund-summary">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">You Pay</span>
                            <span className="font-mono font-bold">${parseFloat(fundAmount).toFixed(2)} {fundCurrency}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">You Receive</span>
                            <span className="font-mono font-bold">
                              ~{fundWalletCurrency === 'CBDC'
                                ? parseFloat(fundAmount).toFixed(2)
                                : (parseFloat(fundAmount) / 5.21).toFixed(2)
                              } {fundWalletCurrency}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-muted-foreground">Via</span>
                            <span className="capitalize font-medium">{fundMethod}</span>
                          </div>
                        </div>
                      )}
                      <Button
                        onClick={handleFundWallet}
                        disabled={fundProcessing || !fundAmount}
                        className="w-full btn-royal-gold"
                        size="lg"
                        data-testid="fund-submit-btn"
                      >
                        {fundProcessing ? 'Processing...' : `Fund Wallet via ${fundMethod === 'stripe' ? 'Stripe' : 'PayPal'}`}
                      </Button>
                    </div>
                  </TabsContent>

                  {/* Withdraw Tab */}
                  <TabsContent value="withdraw">
                    <div className="space-y-5">
                      {bankAccounts.length === 0 ? (
                        <div className="text-center py-8">
                          <Building className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                          <p className="text-muted-foreground mb-4">Link a bank account first to withdraw funds</p>
                          <Button onClick={() => { setActiveTab('banks'); setAddBankOpen(true); }} className="btn-royal-gold" data-testid="add-bank-for-withdraw">
                            <Plus className="h-4 w-4 mr-2" /> Add Bank Account
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>Withdraw From</Label>
                            <Select value={withdrawWalletCurrency} onValueChange={setWithdrawWalletCurrency}>
                              <SelectTrigger data-testid="withdraw-wallet-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CBDC">CBDC Wallet ({cbdcWallet?.balance?.toLocaleString() || 0})</SelectItem>
                                <SelectItem value="TNB">TNB Wallet ({kalWallet?.balance?.toLocaleString() || 0})</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Amount</Label>
                            <Input
                              type="number" step="0.01" placeholder="Enter amount"
                              value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)}
                              data-testid="withdraw-amount-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>To Bank Account</Label>
                            <Select value={withdrawBankId} onValueChange={setWithdrawBankId}>
                              <SelectTrigger data-testid="withdraw-bank-select"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                              <SelectContent>
                                {bankAccounts.map(ba => (
                                  <SelectItem key={ba.account_id} value={ba.account_id}>
                                    {ba.bank_name} ****{ba.account_number_last4} {ba.is_primary ? '(Primary)' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            onClick={handleWithdraw}
                            disabled={withdrawProcessing || !withdrawAmount || !withdrawBankId}
                            className="w-full bg-black hover:bg-black/90 text-white"
                            size="lg"
                            data-testid="withdraw-submit-btn"
                          >
                            {withdrawProcessing ? 'Processing...' : 'Withdraw to Bank'}
                          </Button>
                        </>
                      )}
                    </div>
                  </TabsContent>

                  {/* Bank Accounts Tab */}
                  <TabsContent value="banks">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Linked bank accounts for deposits and withdrawals</p>
                        <Dialog open={addBankOpen} onOpenChange={setAddBankOpen}>
                          <DialogTrigger asChild>
                            <Button size="sm" className="btn-royal-gold gap-1" data-testid="add-bank-btn">
                              <Plus className="h-4 w-4" /> Add Account
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-lg">
                            <DialogHeader>
                              <DialogTitle>Link Bank Account</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddBank} className="space-y-4 mt-2">
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Account Holder Name</Label>
                                  <Input placeholder="John Doe" value={bankForm.account_name}
                                    onChange={e => setBankForm({...bankForm, account_name: e.target.value})} required data-testid="bank-name-input" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Bank Name</Label>
                                  <Input placeholder="Chase, Barclays..." value={bankForm.bank_name}
                                    onChange={e => setBankForm({...bankForm, bank_name: e.target.value})} required data-testid="bank-institution-input" />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Account Number</Label>
                                <Input placeholder="Enter account number" value={bankForm.account_number}
                                  onChange={e => setBankForm({...bankForm, account_number: e.target.value})} required data-testid="bank-account-number-input" />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Routing Number</Label>
                                  <Input placeholder="For US banks" value={bankForm.routing_number}
                                    onChange={e => setBankForm({...bankForm, routing_number: e.target.value})} data-testid="bank-routing-input" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">SWIFT Code</Label>
                                  <Input placeholder="For international" value={bankForm.swift_code}
                                    onChange={e => setBankForm({...bankForm, swift_code: e.target.value})} data-testid="bank-swift-input" />
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Account Type</Label>
                                  <Select value={bankForm.account_type} onValueChange={v => setBankForm({...bankForm, account_type: v})}>
                                    <SelectTrigger data-testid="bank-type-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="checking">Checking</SelectItem>
                                      <SelectItem value="savings">Savings</SelectItem>
                                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Currency</Label>
                                  <Select value={bankForm.currency} onValueChange={v => setBankForm({...bankForm, currency: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="USD">USD</SelectItem>
                                      <SelectItem value="EUR">EUR</SelectItem>
                                      <SelectItem value="GBP">GBP</SelectItem>
                                      <SelectItem value="NGN">NGN</SelectItem>
                                      <SelectItem value="KES">KES</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Country</Label>
                                  <Select value={bankForm.country} onValueChange={v => setBankForm({...bankForm, country: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="US">US</SelectItem>
                                      <SelectItem value="UK">UK</SelectItem>
                                      <SelectItem value="NG">Nigeria</SelectItem>
                                      <SelectItem value="KE">Kenya</SelectItem>
                                      <SelectItem value="GH">Ghana</SelectItem>
                                      <SelectItem value="ZA">South Africa</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <Button type="submit" className="w-full btn-royal-gold" data-testid="bank-submit-btn">
                                Link Bank Account
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>

                      {bankAccounts.length === 0 ? (
                        <div className="text-center py-10">
                          <Landmark className="h-14 w-14 mx-auto mb-4 text-muted-foreground/40" />
                          <p className="text-muted-foreground">No bank accounts linked yet</p>
                          <p className="text-sm text-muted-foreground/70 mt-1">Add a bank account to receive payments and withdrawals</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {bankAccounts.map(ba => (
                            <div key={ba.account_id} className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:border-gold/20 transition-colors" data-testid={`bank-account-${ba.account_id}`}>
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-black/5 flex items-center justify-center">
                                  {ba.account_type === 'mobile_money'
                                    ? <Smartphone className="h-5 w-5" />
                                    : <Building className="h-5 w-5" />}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-sm">{ba.bank_name}</p>
                                    {ba.is_primary && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-gold/10 text-gold font-medium">Primary</span>
                                    )}
                                    {ba.is_verified && <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />}
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {ba.account_name} | ****{ba.account_number_last4} | {ba.account_type} | {ba.currency}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!ba.is_primary && (
                                  <Button variant="ghost" size="icon" onClick={() => handleSetPrimary(ba.account_id)} title="Set as primary" data-testid={`set-primary-${ba.account_id}`}>
                                    <Star className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" onClick={() => handleDeleteBank(ba.account_id)} className="text-red-500 hover:text-red-600" data-testid={`delete-bank-${ba.account_id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Methods Info */}
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-gold" /> Supported Methods
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { icon: <CreditCard className="h-4 w-4" />, title: 'Stripe', desc: 'Credit/Debit Card, ACH Bank Transfer' },
                  { icon: <Globe className="h-4 w-4" />, title: 'PayPal', desc: 'PayPal Balance, Linked Bank' },
                  { icon: <Building className="h-4 w-4" />, title: 'Bank Transfer', desc: 'Direct wire / ACH transfer' },
                  { icon: <Smartphone className="h-4 w-4" />, title: 'Mobile Money', desc: 'M-Pesa, MTN, Airtel Money' },
                  { icon: <Globe className="h-4 w-4" />, title: 'International Wire', desc: 'SWIFT transfers worldwide' },
                ].map((m, i) => (
                  <div key={i} className="flex items-start gap-3 p-2">
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center shrink-0">{m.icon}</div>
                    <div>
                      <p className="text-sm font-medium">{m.title}</p>
                      <p className="text-xs text-muted-foreground">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Recent Payments */}
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="text-lg">Recent Payments</CardTitle>
              </CardHeader>
              <CardContent>
                {paymentHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No payment history yet</p>
                ) : (
                  <div className="space-y-2">
                    {paymentHistory.slice(0, 5).map((p, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0" data-testid={`payment-${i}`}>
                        <div>
                          <p className="text-sm font-medium capitalize">{p.payment_type?.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{p.payment_method} | {p.wallet_currency}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm font-bold">${p.amount}</p>
                          <p className={`text-xs ${p.payment_status === 'completed' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {p.payment_status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PaymentsPage;
